import { fitSpriteSize, getFrameSize, getSpriteSourceSize, getSpriteUvArray } from "./utils.js";

const canvas = document.getElementById("glcanvas");
const gl = canvas.getContext("webgl", { alpha: false, antialias: true, depth: true });
if (!gl) {
  alert("WebGL is not supported on this device.");
  throw new Error("WebGL not supported.");
}

const vertexSource = `
  attribute vec2 a_position;
  attribute vec2 a_texcoord;
  attribute vec4 a_color;
  varying vec2 v_texcoord;
  varying vec4 v_color;
  void main() {
    gl_Position = vec4(a_position, 0.0, 1.0);
    v_texcoord = a_texcoord;
    v_color = a_color;
  }
`;

const fragmentSource = `
  precision mediump float;
  varying vec2 v_texcoord;
  varying vec4 v_color;
  uniform sampler2D u_texture;
  void main() {
    vec4 tex = texture2D(u_texture, v_texcoord);
    gl_FragColor = tex * v_color;
  }
`;

const program = createProgram(gl, vertexSource, fragmentSource);
const positionLoc = gl.getAttribLocation(program, "a_position");
const texcoordLoc = gl.getAttribLocation(program, "a_texcoord");
const colorLoc = gl.getAttribLocation(program, "a_color");
const textureLoc = gl.getUniformLocation(program, "u_texture");

const positionBuffer = gl.createBuffer();
const texcoordBuffer = gl.createBuffer();
const colorBuffer = gl.createBuffer();
const whiteTexture = createSolidTexture(gl, [255, 255, 255, 255]);

gl.useProgram(program);
gl.uniform1i(textureLoc, 0);
gl.disable(gl.DEPTH_TEST);
gl.enable(gl.BLEND);
gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

const MAX_BATCH_QUADS = 2048;
const spriteBatch = {
  active: false,
  texture: null,
  quadCount: 0,
  positions: new Float32Array(MAX_BATCH_QUADS * 6 * 2),
  texcoords: new Float32Array(MAX_BATCH_QUADS * 6 * 2),
  colors: new Float32Array(MAX_BATCH_QUADS * 6 * 4),
};

function createSolidTexture(glContext, rgba) {
  const texture = glContext.createTexture();
  glContext.bindTexture(glContext.TEXTURE_2D, texture);
  const data = new Uint8Array(rgba);
  glContext.texImage2D(
    glContext.TEXTURE_2D,
    0,
    glContext.RGBA,
    1,
    1,
    0,
    glContext.RGBA,
    glContext.UNSIGNED_BYTE,
    data
  );
  glContext.texParameteri(glContext.TEXTURE_2D, glContext.TEXTURE_WRAP_S, glContext.CLAMP_TO_EDGE);
  glContext.texParameteri(glContext.TEXTURE_2D, glContext.TEXTURE_WRAP_T, glContext.CLAMP_TO_EDGE);
  glContext.texParameteri(glContext.TEXTURE_2D, glContext.TEXTURE_MIN_FILTER, glContext.NEAREST);
  glContext.texParameteri(glContext.TEXTURE_2D, glContext.TEXTURE_MAG_FILTER, glContext.NEAREST);
  return texture;
}

export function createImageTexture(glContext, img) {
  const texture = glContext.createTexture();
  glContext.bindTexture(glContext.TEXTURE_2D, texture);
  glContext.pixelStorei(glContext.UNPACK_FLIP_Y_WEBGL, true);
  glContext.texImage2D(
    glContext.TEXTURE_2D,
    0,
    glContext.RGBA,
    glContext.RGBA,
    glContext.UNSIGNED_BYTE,
    img
  );
  glContext.texParameteri(glContext.TEXTURE_2D, glContext.TEXTURE_WRAP_S, glContext.CLAMP_TO_EDGE);
  glContext.texParameteri(glContext.TEXTURE_2D, glContext.TEXTURE_WRAP_T, glContext.CLAMP_TO_EDGE);
  glContext.texParameteri(glContext.TEXTURE_2D, glContext.TEXTURE_MIN_FILTER, glContext.LINEAR);
  glContext.texParameteri(glContext.TEXTURE_2D, glContext.TEXTURE_MAG_FILTER, glContext.LINEAR);
  return texture;
}

function createProgram(glContext, vertexSrc, fragmentSrc) {
  const vertexShader = compileShader(glContext, glContext.VERTEX_SHADER, vertexSrc);
  const fragmentShader = compileShader(glContext, glContext.FRAGMENT_SHADER, fragmentSrc);
  const program = glContext.createProgram();
  glContext.attachShader(program, vertexShader);
  glContext.attachShader(program, fragmentShader);
  glContext.linkProgram(program);

  if (!glContext.getProgramParameter(program, glContext.LINK_STATUS)) {
    const info = glContext.getProgramInfoLog(program);
    glContext.deleteProgram(program);
    throw new Error(`WebGL program failed to link: ${info}`);
  }
  return program;
}

function compileShader(glContext, type, source) {
  const shader = glContext.createShader(type);
  glContext.shaderSource(shader, source);
  glContext.compileShader(shader);

  if (!glContext.getShaderParameter(shader, glContext.COMPILE_STATUS)) {
    const info = glContext.getShaderInfoLog(shader);
    glContext.deleteShader(shader);
    throw new Error(`WebGL shader failed to compile: ${info}`);
  }
  return shader;
}

export function resizeCanvasToDisplaySize() {
  const dpr = Math.max(1, window.devicePixelRatio || 1);
  const displayWidth = Math.floor(canvas.clientWidth * dpr);
  const displayHeight = Math.floor(canvas.clientHeight * dpr);
  if (canvas.width !== displayWidth || canvas.height !== displayHeight) {
    canvas.width = displayWidth;
    canvas.height = displayHeight;
    gl.viewport(0, 0, canvas.width, canvas.height);
    return true;
  }
  return false;
}

export function drawQuad(positions, texcoords, texture, color) {
  if (spriteBatch.active) {
    flushSpriteBatch();
  }
  gl.useProgram(program);

  gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
  gl.bufferData(gl.ARRAY_BUFFER, positions, gl.DYNAMIC_DRAW);
  gl.enableVertexAttribArray(positionLoc);
  gl.vertexAttribPointer(positionLoc, 2, gl.FLOAT, false, 0, 0);

  gl.bindBuffer(gl.ARRAY_BUFFER, texcoordBuffer);
  gl.bufferData(gl.ARRAY_BUFFER, texcoords, gl.DYNAMIC_DRAW);
  gl.enableVertexAttribArray(texcoordLoc);
  gl.vertexAttribPointer(texcoordLoc, 2, gl.FLOAT, false, 0, 0);

  gl.disableVertexAttribArray(colorLoc);
  gl.vertexAttrib4f(colorLoc, color[0], color[1], color[2], color[3]);

  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, texture);

  gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
}

function getTrimmedRect(rect, sprite) {
  if (!sprite) {
    return rect;
  }
  const sourceWidth = sprite.sourceWidth || sprite.width || rect.width;
  const sourceHeight = sprite.sourceHeight || sprite.height || rect.height;
  const trimWidth = sprite.trimWidth || sourceWidth;
  const trimHeight = sprite.trimHeight || sourceHeight;
  const trimX = sprite.trimX || 0;
  const trimY = sprite.trimY || 0;
  if (
    trimX === 0 &&
    trimY === 0 &&
    trimWidth === sourceWidth &&
    trimHeight === sourceHeight
  ) {
    return rect;
  }
  return {
    left: rect.left + (trimX / sourceWidth) * rect.width,
    bottom: rect.bottom + (1 - (trimY + trimHeight) / sourceHeight) * rect.height,
    width: rect.width * (trimWidth / sourceWidth),
    height: rect.height * (trimHeight / sourceHeight),
  };
}

export function drawSpriteRect(textureInfo, rect, color = [1, 1, 1, 1]) {
  if (!textureInfo || !textureInfo.texture) {
    return;
  }
  const drawRect = getTrimmedRect(rect, textureInfo);
  if (spriteBatch.active) {
    batchSpriteQuad(textureInfo, drawRect, color);
    return;
  }
  const quad = rectToNdcQuad(drawRect);
  drawQuad(quad, getSpriteUvArray(textureInfo), textureInfo.texture, color);
}

export function beginSpriteBatch() {
  spriteBatch.active = true;
  spriteBatch.texture = null;
  spriteBatch.quadCount = 0;
}

export function endSpriteBatch() {
  flushSpriteBatch();
  spriteBatch.active = false;
}

export function flushSpriteBatch() {
  if (!spriteBatch.active || !spriteBatch.texture || spriteBatch.quadCount === 0) {
    return;
  }
  const vertexCount = spriteBatch.quadCount * 6;
  gl.useProgram(program);

  gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
  gl.bufferData(
    gl.ARRAY_BUFFER,
    spriteBatch.positions.subarray(0, vertexCount * 2),
    gl.DYNAMIC_DRAW
  );
  gl.enableVertexAttribArray(positionLoc);
  gl.vertexAttribPointer(positionLoc, 2, gl.FLOAT, false, 0, 0);

  gl.bindBuffer(gl.ARRAY_BUFFER, texcoordBuffer);
  gl.bufferData(
    gl.ARRAY_BUFFER,
    spriteBatch.texcoords.subarray(0, vertexCount * 2),
    gl.DYNAMIC_DRAW
  );
  gl.enableVertexAttribArray(texcoordLoc);
  gl.vertexAttribPointer(texcoordLoc, 2, gl.FLOAT, false, 0, 0);

  gl.bindBuffer(gl.ARRAY_BUFFER, colorBuffer);
  gl.bufferData(
    gl.ARRAY_BUFFER,
    spriteBatch.colors.subarray(0, vertexCount * 4),
    gl.DYNAMIC_DRAW
  );
  gl.enableVertexAttribArray(colorLoc);
  gl.vertexAttribPointer(colorLoc, 4, gl.FLOAT, false, 0, 0);

  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, spriteBatch.texture);
  gl.drawArrays(gl.TRIANGLES, 0, vertexCount);

  spriteBatch.quadCount = 0;
}

function batchSpriteQuad(textureInfo, rect, color) {
  if (!textureInfo || !textureInfo.texture) {
    return;
  }
  if (spriteBatch.texture !== textureInfo.texture) {
    flushSpriteBatch();
    spriteBatch.texture = textureInfo.texture;
  }
  if (spriteBatch.quadCount >= MAX_BATCH_QUADS) {
    flushSpriteBatch();
  }
  const left = (rect.left / canvas.width) * 2 - 1;
  const right = ((rect.left + rect.width) / canvas.width) * 2 - 1;
  const bottom = (rect.bottom / canvas.height) * 2 - 1;
  const top = ((rect.bottom + rect.height) / canvas.height) * 2 - 1;

  let u0 = 0;
  let v0 = 0;
  let u1 = 1;
  let v1 = 1;
  if (textureInfo.uv) {
    u0 = textureInfo.uv.u0;
    v0 = textureInfo.uv.v0;
    u1 = textureInfo.uv.u1;
    v1 = textureInfo.uv.v1;
  }
  const quadIndex = spriteBatch.quadCount;
  const posIndex = quadIndex * 12;
  const uvIndex = quadIndex * 12;
  const colorIndex = quadIndex * 24;

  const positions = spriteBatch.positions;
  const texcoords = spriteBatch.texcoords;
  const colors = spriteBatch.colors;

  positions[posIndex] = left;
  positions[posIndex + 1] = bottom;
  positions[posIndex + 2] = right;
  positions[posIndex + 3] = bottom;
  positions[posIndex + 4] = left;
  positions[posIndex + 5] = top;
  positions[posIndex + 6] = left;
  positions[posIndex + 7] = top;
  positions[posIndex + 8] = right;
  positions[posIndex + 9] = bottom;
  positions[posIndex + 10] = right;
  positions[posIndex + 11] = top;

  texcoords[uvIndex] = u0;
  texcoords[uvIndex + 1] = v0;
  texcoords[uvIndex + 2] = u1;
  texcoords[uvIndex + 3] = v0;
  texcoords[uvIndex + 4] = u0;
  texcoords[uvIndex + 5] = v1;
  texcoords[uvIndex + 6] = u0;
  texcoords[uvIndex + 7] = v1;
  texcoords[uvIndex + 8] = u1;
  texcoords[uvIndex + 9] = v0;
  texcoords[uvIndex + 10] = u1;
  texcoords[uvIndex + 11] = v1;

  for (let i = 0; i < 6; i += 1) {
    const base = colorIndex + i * 4;
    colors[base] = color[0];
    colors[base + 1] = color[1];
    colors[base + 2] = color[2];
    colors[base + 3] = color[3];
  }

  spriteBatch.quadCount += 1;
}

export function drawTriangles(positions, texcoords, color) {
  if (spriteBatch.active) {
    flushSpriteBatch();
  }
  gl.useProgram(program);
  gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
  gl.bufferData(gl.ARRAY_BUFFER, positions, gl.DYNAMIC_DRAW);
  gl.enableVertexAttribArray(positionLoc);
  gl.vertexAttribPointer(positionLoc, 2, gl.FLOAT, false, 0, 0);

  gl.bindBuffer(gl.ARRAY_BUFFER, texcoordBuffer);
  gl.bufferData(gl.ARRAY_BUFFER, texcoords, gl.DYNAMIC_DRAW);
  gl.enableVertexAttribArray(texcoordLoc);
  gl.vertexAttribPointer(texcoordLoc, 2, gl.FLOAT, false, 0, 0);

  gl.disableVertexAttribArray(colorLoc);
  gl.vertexAttrib4f(colorLoc, color[0], color[1], color[2], color[3]);

  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, whiteTexture);

  gl.drawArrays(gl.TRIANGLES, 0, positions.length / 2);
}

export function rectToNdcQuad(rect, canvasWidth = canvas.width, canvasHeight = canvas.height) {
  const left = (rect.left / canvasWidth) * 2 - 1;
  const right = ((rect.left + rect.width) / canvasWidth) * 2 - 1;
  const bottom = (rect.bottom / canvasHeight) * 2 - 1;
  const top = ((rect.bottom + rect.height) / canvasHeight) * 2 - 1;
  return new Float32Array([left, bottom, right, bottom, left, top, right, top]);
}

export function lineToNdcQuad(start, end, thickness, canvasWidth = canvas.width, canvasHeight = canvas.height) {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const len = Math.hypot(dx, dy);
  if (len === 0) {
    return null;
  }
  const nx = -dy / len;
  const ny = dx / len;
  const half = thickness * 0.5;
  const offsetX = nx * half;
  const offsetY = ny * half;

  const p0 = { x: start.x + offsetX, y: start.y + offsetY };
  const p1 = { x: start.x - offsetX, y: start.y - offsetY };
  const p2 = { x: end.x + offsetX, y: end.y + offsetY };
  const p3 = { x: end.x - offsetX, y: end.y - offsetY };

  const toNdc = (p) => ({
    x: (p.x / canvasWidth) * 2 - 1,
    y: (p.y / canvasHeight) * 2 - 1,
  });
  const n0 = toNdc(p0);
  const n1 = toNdc(p1);
  const n2 = toNdc(p2);
  const n3 = toNdc(p3);
  return new Float32Array([n0.x, n0.y, n1.x, n1.y, n2.x, n2.y, n3.x, n3.y]);
}

export function drawSprite(textureInfo, center, baseSize, scaleX, scaleY) {
  if (!textureInfo || !textureInfo.texture) {
    return;
  }
  const sourceSize = getSpriteSourceSize(textureInfo);
  const fitted = fitSpriteSize(baseSize, sourceSize.width, sourceSize.height);
  const width = fitted.width * scaleX;
  const height = fitted.height * scaleY;
  const rect = {
    left: center.x - width * 0.5,
    bottom: center.y - height * 0.5,
    width,
    height,
  };
  drawSpriteRect(textureInfo, rect, [1, 1, 1, 1]);
}

export function drawSpriteRotated(textureInfo, center, baseSize, scaleX, scaleY, rotation) {
  if (!textureInfo || !textureInfo.texture) {
    return;
  }
  const sourceSize = getSpriteSourceSize(textureInfo);
  const fitted = fitSpriteSize(baseSize, sourceSize.width, sourceSize.height);
  const width = fitted.width * scaleX;
  const height = fitted.height * scaleY;
  const sourceWidth = sourceSize.width || width;
  const sourceHeight = sourceSize.height || height;
  const trimWidth = textureInfo.trimWidth || sourceWidth;
  const trimHeight = textureInfo.trimHeight || sourceHeight;
  const trimX = textureInfo.trimX || 0;
  const trimY = textureInfo.trimY || 0;
  const trimLeft = -width * 0.5 + (trimX / sourceWidth) * width;
  const trimBottom = -height * 0.5 + (1 - (trimY + trimHeight) / sourceHeight) * height;
  const trimW = width * (trimWidth / sourceWidth);
  const trimH = height * (trimHeight / sourceHeight);
  const cos = Math.cos(rotation);
  const sin = Math.sin(rotation);
  const corners = [
    { x: trimLeft, y: trimBottom },
    { x: trimLeft + trimW, y: trimBottom },
    { x: trimLeft, y: trimBottom + trimH },
    { x: trimLeft + trimW, y: trimBottom + trimH },
  ];
  const positions = new Float32Array(8);
  for (let i = 0; i < corners.length; i += 1) {
    const cx = corners[i].x;
    const cy = corners[i].y;
    const rx = cx * cos - cy * sin + center.x;
    const ry = cx * sin + cy * cos + center.y;
    positions[i * 2] = (rx / canvas.width) * 2 - 1;
    positions[i * 2 + 1] = (ry / canvas.height) * 2 - 1;
  }
  drawQuad(positions, getSpriteUvArray(textureInfo), textureInfo.texture, [1, 1, 1, 1]);
}

export function drawSpriteFrame(textureInfo, frameIndex, cols, rows, rect) {
  if (!textureInfo || !textureInfo.texture) {
    return;
  }
  const frame = Math.max(0, Math.min(frameIndex, cols * rows - 1));
  const col = frame % cols;
  const row = Math.floor(frame / cols);
  const frameSize = getFrameSize(textureInfo, cols, rows);
  if (!frameSize) {
    return;
  }
  const localU0 = (col * frameSize.width) / textureInfo.width;
  const localU1 = ((col + 1) * frameSize.width) / textureInfo.width;
  const vTop = (row * frameSize.height) / textureInfo.height;
  const vBottom = ((row + 1) * frameSize.height) / textureInfo.height;
  const localV0 = 1 - vBottom;
  const localV1 = 1 - vTop;
  let u0 = localU0;
  let u1 = localU1;
  let v0 = localV0;
  let v1 = localV1;
  if (textureInfo.uv) {
    const uv = textureInfo.uv;
    u0 = uv.u0 + (uv.u1 - uv.u0) * localU0;
    u1 = uv.u0 + (uv.u1 - uv.u0) * localU1;
    v0 = uv.v0 + (uv.v1 - uv.v0) * localV0;
    v1 = uv.v0 + (uv.v1 - uv.v0) * localV1;
  }
  const quad = rectToNdcQuad(rect);
  drawQuad(
    quad,
    new Float32Array([u0, v0, u1, v0, u0, v1, u1, v1]),
    textureInfo.texture,
    [1, 1, 1, 1]
  );
}

export {
  canvas,
  gl,
  program,
  positionLoc,
  texcoordLoc,
  textureLoc,
  colorLoc,
  whiteTexture,
};
